import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import { FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Fan } from '@sorare/core/src/components/animations/Fan';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import { unitMapping } from '@sorare/core/src/lib/style';

import { ClickableAnyRewardConfig } from '#src/components/rewards/ClickableAnyRewardConfig/index.tsx';

import type { Rewards_anyRewardConfig } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  position: relative;
  isolation: isolate;
`;
const AnyRewardConfigWrapper = styled.div`
  &.iconOnly {
    width: var(--icon-size);
  }
`;
const ShadowText = styled(LabelS)`
  position: absolute;
  bottom: 0;
  right: 0;
  text-shadow:
    -1px -1px 0 var(--c-black),
    1px -1px 0 var(--c-black),
    -1px 1px 0 var(--c-black),
    1px 1px 0 var(--c-black);
  @supports (paint-order: stroke fill) {
    text-shadow: none;
    -webkit-text-stroke: 3px var(--c-black);
    paint-order: stroke fill;
  }
`;
type Props = {
  rewardConfigs: Rewards_anyRewardConfig[];
  variant?: Variant | 'VALUE_ONLY';
  rarity?: Rarity;
  clickable?: boolean;
  iconSize?: keyof typeof unitMapping;
};
export const Rewards = ({
  rewardConfigs,
  variant,
  rarity,
  clickable = true,
  iconSize = 8,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const rewardsGroupedByType = groupBy(rewardConfigs, r => r.__typename);

  const rewardCountsByType = Object.entries(rewardsGroupedByType).reduce<
    Record<string, number>
  >((acc, [typename, rewards]) => {
    acc[typename] = rewards.reduce<number>((sum, curr) => {
      if (isType(curr, 'InGameCurrencyRewardConfig')) {
        return sum + curr.amount;
      }
      if (isType(curr, 'CardShardRewardConfig')) {
        return sum + curr.quantity;
      }
      return sum;
    }, 0);
    return acc;
  }, {});

  const amount = Sum(
    rewardConfigs
      .map(curr => {
        const isCashReward = isType(curr, 'MonetaryRewardConfig');
        const isCCReward = isType(curr, 'ConversionCreditRewardConfig');
        if (isCashReward || isCCReward) {
          return isCashReward
            ? toMonetaryAmount(curr.monetaryAmount)
            : toMonetaryAmount(curr.maxDiscount);
        }
        return undefined;
      })
      .filter(Boolean)
  ).usd;

  const renderText = () => {
    if (amount) {
      const hashBoth =
        rewardConfigs.find(config => isType(config, 'MonetaryRewardConfig')) &&
        rewardConfigs.find(config =>
          isType(config, 'ConversionCreditRewardConfig')
        );
      if (hashBoth) {
        return (
          <>
            <FormattedNumber
              style="currency"
              currency="USD"
              currencyDisplay="narrowSymbol"
              compactDisplay="short"
              notation="compact"
              value={amount}
            />
            {hashBoth ? '*' : ''}
          </>
        );
      }
      return (
        <FormattedNumber
          style="currency"
          currency="USD"
          currencyDisplay="narrowSymbol"
          maximumFractionDigits={0}
          value={amount}
        />
      );
    }

    if (
      Object.keys(rewardCountsByType).length === 1 &&
      (Object.keys(rewardCountsByType)[0] === 'InGameCurrencyRewardConfig' ||
        Object.keys(rewardCountsByType)[0] === 'CardShardRewardConfig')
    ) {
      return <FormattedNumber value={Object.values(rewardCountsByType)[0]} />;
    }

    return (
      <>
        {[...rewardConfigs].reverse().map(config => (
          <RewardConfigText key={config.id} anyRewardConfig={config} />
        ))}
      </>
    );
  };

  if (variant === 'VALUE_ONLY') {
    return renderText();
  }

  const RewardConfigComponent = clickable
    ? ClickableAnyRewardConfig
    : AnyRewardConfig;

  return (
    <Root style={{ '--icon-size': unitMapping[iconSize] } as CSSProperties}>
      <ConditionalWrapper Wrapper={Fan} wrap={variant === Variant.TEXT}>
        {[...rewardConfigs].reverse().map(config => (
          <AnyRewardConfigWrapper
            className={classnames(variant)}
            key={config.id}
          >
            {variant === Variant.TEXT ? (
              <RewardConfigImage size={6} anyRewardConfig={config} />
            ) : (
              <RewardConfigComponent
                anyRewardConfig={config}
                rarity={rarity}
                variant={variant}
              />
            )}
          </AnyRewardConfigWrapper>
        ))}
      </ConditionalWrapper>
      {variant === Variant.TEXT && <ShadowText bold>{renderText()}</ShadowText>}
    </Root>
  );
};
Rewards.fragments = {
  anyRewardConfig: gql`
    fragment Rewards_anyRewardConfig on AnyRewardConfigInterface {
      id
      ...ClickableAnyRewardConfig_anyRewardConfigInterface
      ...AnyRewardConfig_anyRewardConfigInterface
      ... on MonetaryRewardConfig {
        id
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on ConversionCreditRewardConfig {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
        percentageDiscount
      }
      ... on InGameCurrencyRewardConfig {
        id
        amount
      }
      ...RewardConfigText_anyRewardConfigInterface
    }
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${
      // eslint-disable-next-line sorare/no-detached-fragments
      ClickableAnyRewardConfig.fragments.anyRewardConfigInterface
    }
    ${
      // eslint-disable-next-line sorare/no-detached-fragments
      AnyRewardConfig.fragments.anyRewardConfigInterface
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<Rewards_anyRewardConfig>,
};
